import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api';

/** Загрузка данных экрана: один запрос, состояние и перезагрузка. */
export function useApi<T>(path: string | null): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  setData: (next: T) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .get<T>(path, controller.signal)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Не удалось загрузить данные');
        setLoading(false);
      });

    return () => controller.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload, setData };
}

/** Задержка ввода в поиске. */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Автосохранение черновика: копит правки и отправляет пачкой.
 * Черновик тренировки сохраняется сам (SPEC §5 T1–T3).
 */
export function useAutosave<T>(
  save: (value: T) => Promise<void>,
  delay = 900,
): { schedule: (value: T) => void; flush: () => Promise<void>; saving: boolean } {
  const [saving, setSaving] = useState(false);
  // Capture the writer with its value, never retarget queued data after a render.
  const pending = useRef<{ value: T; save: typeof save } | null>(null);
  const active = useRef<Promise<void> | null>(null);
  const mounted = useRef(true);
  const timer = useRef<number | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const run = useCallback((): Promise<void> => {
    if (!mounted.current) return Promise.reject(new Error('Автосохранение отменено'));
    if (active.current) return active.current;
    if (!pending.current) return Promise.resolve();
    setSaving(true);
    // Install the shared promise before invoking any writer. One drain owns all writes.
    const request = Promise.resolve().then(async () => {
      while (pending.current && mounted.current) {
        const item = pending.current;
        pending.current = null;
        try {
          await item.save(item.value);
        } catch (error) {
          // Keep the newest draft for an explicit retry, never spin on a failed write.
          if (mounted.current && !pending.current) pending.current = item;
          if (timer.current !== null) window.clearTimeout(timer.current);
          timer.current = null;
          throw error;
        }
      }
      if (!mounted.current) throw new Error('Автосохранение отменено');
    }).finally(() => {
      active.current = null;
      if (mounted.current) setSaving(false);
    });
    active.current = request;
    return request;
  }, []);

  const schedule = useCallback(
    (value: T) => {
      if (!mounted.current) return;
      pending.current = { value, save: saveRef.current };
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        // Explicit flush callers still observe rejection; background failure stays dirty.
        void run().catch(() => {});
      }, delay);
    },
    [delay, run],
  );

  const flush = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    await run();
  }, [run]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pending.current = null;
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    };
  }, []);

  return { schedule, flush, saving };
}
